import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const FixedCostsSection = ({
  fixedCosts,
  onAddFixedCost,
  onRemoveFixedCost,
  onFixedCostChange,
  formatCurrency,
  readOnly,
  saving,
  deptFilter,
  departments,
  locationOptions
}) => {
  const [deptPopoverAnchor, setDeptPopoverAnchor] = useState(null);
  const [activeCostId, setActiveCostId] = useState(null);

  // When a dept filter is active, show only untagged costs + costs that include the active dept
  const visibleCosts = deptFilter
    ? fixedCosts.filter(c => !c.departments || c.departments.length === 0 || c.departments.includes(deptFilter))
    : fixedCosts;

  const totalFixedCosts = visibleCosts.reduce((sum, cost) => {
    const startDate = dayjs(cost.start_date + '-01');
    const endDate = dayjs(cost.end_date + '-01');
    const duration = Math.max(1, endDate.diff(startDate, 'month') + 1);
    return sum + ((cost.monthlyCost || 0) * duration);
  }, 0);

  const handleOpenDeptPopover = (e, costId) => {
    e.stopPropagation();
    setActiveCostId(costId);
    setDeptPopoverAnchor(e.currentTarget);
  };

  const handleCloseDeptPopover = () => {
    setDeptPopoverAnchor(null);
    setActiveCostId(null);
  };

  const handleToggleDept = (deptName) => {
    const cost = fixedCosts.find(c => c.id === activeCostId);
    if (!cost) return;
    const current = cost.departments || [];
    const updated = current.includes(deptName)
      ? current.filter(d => d !== deptName)
      : [...current, deptName];
    onFixedCostChange(activeCostId, 'departments', updated);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AttachMoneyIcon color="primary" />
            <Typography variant="h6">
              Fixed Costs ({deptFilter && visibleCosts.length < fixedCosts.length
                ? `${visibleCosts.length}/${fixedCosts.length}`
                : fixedCosts.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total: {formatCurrency(totalFixedCosts)}
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddFixedCost}
            disabled={readOnly || saving}
            size="small"
          >
            Add Fixed Cost
          </Button>
        </Box>

        {fixedCosts.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Departments</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Monthly Cost</TableCell>
                  <TableCell align="right">Total Cost</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCosts.map((cost) => {
                  const startDate = dayjs(cost.start_date + '-01');
                  const endDate = dayjs(cost.end_date + '-01');
                  const duration = Math.max(1, endDate.diff(startDate, 'month') + 1);
                  const totalCost = (cost.monthlyCost || 0) * duration;

                  return (
                    <TableRow key={cost.id}>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={cost.type || 'Licenses'}
                            onChange={(e) => onFixedCostChange(cost.id, 'type', e.target.value)}
                            disabled={readOnly}
                          >
                            <MenuItem value="Licenses">Licenses</MenuItem>
                            <MenuItem value="Hardware">Hardware</MenuItem>
                            <MenuItem value="Software">Software</MenuItem>
                            <MenuItem value="Services">Services</MenuItem>
                            <MenuItem value="Training">Training</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      
                      <TableCell>
                        <TextField
                          size="small"
                          value={cost.details || ''}
                          onChange={(e) => onFixedCostChange(cost.id, 'details', e.target.value)}
                          placeholder="Description..."
                          disabled={readOnly}
                          sx={{ minWidth: 150 }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={cost.company || ''}
                            onChange={(e) => onFixedCostChange(cost.id, 'company', e.target.value)}
                            disabled={readOnly}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {locationOptions && Object.keys(locationOptions).length > 0
                              ? Object.keys(locationOptions).map(co => (
                                  <MenuItem key={co} value={co}>{co}</MenuItem>
                                ))
                              : ['CA', 'GFT'].map(co => (
                                  <MenuItem key={co} value={co}>{co}</MenuItem>
                                ))
                            }
                          </Select>
                        </FormControl>
                      </TableCell>

                      {/* Department tags */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', minWidth: 80 }}>
                          {(cost.departments || []).map(dept => {
                            const deptLabel = typeof dept === 'object' ? dept.name : dept;
                            return (
                              <Chip
                                key={deptLabel}
                                label={deptLabel}
                                size="small"
                                color="info"
                                variant="outlined"
                                onDelete={readOnly ? undefined : () => {
                                  const updated = (cost.departments || []).filter(d => (typeof d === 'object' ? d.name : d) !== deptLabel);
                                  onFixedCostChange(cost.id, 'departments', updated);
                                }}
                                sx={{ fontSize: '0.6rem', height: 16 }}
                              />
                            );
                          })}
                          {!readOnly && (
                            <Chip
                              size="small"
                              label="+"
                              variant="outlined"
                              onClick={(e) => handleOpenDeptPopover(e, cost.id)}
                              sx={{ fontSize: '0.6rem', height: 16, cursor: 'pointer' }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            views={['year', 'month']}
                            value={dayjs(cost.start_date + '-01')}
                            onChange={(newValue) => 
                              onFixedCostChange(cost.id, 'start_date', newValue.format('YYYY-MM'))
                            }
                            disabled={readOnly}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { minWidth: 120 }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </TableCell>
                      
                      <TableCell>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            views={['year', 'month']}
                            value={dayjs(cost.end_date + '-01')}
                            onChange={(newValue) => 
                              onFixedCostChange(cost.id, 'end_date', newValue.format('YYYY-MM'))
                            }
                            disabled={readOnly}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { minWidth: 120 }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </TableCell>
                      
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={cost.monthlyCost || 0}
                          onChange={(e) => onFixedCostChange(cost.id, 'monthlyCost', Number(e.target.value))}
                          disabled={readOnly}
                          sx={{ width: 100 }}
                          inputProps={{ min: 0, step: 100 }}
                        />
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(totalCost)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {duration} months
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={cost.capex_classification || 'OPEX'}
                            onChange={(e) => onFixedCostChange(cost.id, 'capex_classification', e.target.value)}
                            disabled={readOnly}
                          >
                            <MenuItem value="CAPEX">CAPEX</MenuItem>
                            <MenuItem value="OPEX">OPEX</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => onRemoveFixedCost(cost.id)}
                          disabled={readOnly}
                          color="error"
                        >
                          <DeleteIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
            <AttachMoneyIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">
              No fixed costs added yet
            </Typography>
            <Typography variant="caption">
              Add licenses, hardware, or other fixed costs
            </Typography>
          </Box>
        )}

      {/* Dept tag popover — shared across all fixed cost rows */}
      <Popover
        open={Boolean(deptPopoverAnchor)}
        anchorEl={deptPopoverAnchor}
        onClose={handleCloseDeptPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 180 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            Tag fixed cost departments
          </Typography>
          <List dense disablePadding>
            {(departments || []).map(d => {
              const activeCost = fixedCosts.find(c => c.id === activeCostId);
              const checked = (activeCost?.departments || []).includes(d.name);
              return (
                <ListItem key={d.id} disablePadding>
                  <ListItemButton onClick={() => handleToggleDept(d.name)} dense>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple size="small" />
                    </ListItemIcon>
                    <ListItemText primary={d.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {(!departments || departments.length === 0) && (
              <ListItem>
                <ListItemText primary="No departments configured" primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
      </CardContent>
    </Card>
  );
};

export default FixedCostsSection;